import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil, Trash2, Users } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n } from "i18n";
import api from "lib/api";

export const ROLE_LABEL_KEY = {
  user: "account.role.user",
  operator: "account.role.operator",
  admin: "account.role.admin",
};

export default function Accounts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { confirm, alert } = useDialog();
  const { t, tf } = useI18n();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const canManage = !!user?.capabilities?.manage_users;

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get("/api/accounts/");
      setAccounts(res.data);
    } catch {
      // erro tratado na UI pela lista vazia
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) fetchAccounts();
  }, [fetchAccounts, canManage]);

  // Menor privilegio: quem nao gerencia usuarios nem enxerga a tela.
  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleDelete = async (account) => {
    let failed = false;
    const confirmed = await confirm({
      title: t("account.deleteTitle"),
      description: tf("account.deleteBody", {
        name: account.first_name || account.email,
      }),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/accounts/${account.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      fetchAccounts();
      return;
    }
    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  return (
    <div className="w-full animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("account.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("account.subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/app/admin/accounts/new")}>
          <Plus className="h-4 w-4 mr-2" />
          {t("account.new")}
        </Button>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("account.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("common.email")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("common.firstName")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("account.role")}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr
                      key={account.id}
                      onClick={() => navigate(`/app/admin/accounts/${account.id}`)}
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium">{account.email}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {[account.first_name, account.last_name].filter(Boolean).join(" ") || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-muted border border-border">
                          {t(ROLE_LABEL_KEY[account.role] || "account.role.user")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => navigate(`/app/admin/accounts/${account.id}`)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.details")}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/app/admin/accounts/${account.id}/edit`)}
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(account)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600"
                            title={t("common.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
